import React from "react";
import { translateService } from "../../services/imports";
import { LoadingButton } from "../../components/formcontrols/imports";
import { methodTitle as title, methodKind as kindLabel, methodHint } from "./signingMethods";

const t = (k, ...a) => translateService.t(`sign.${k}`, ...a);

// what a choice means; a disabled one says why it cannot serve
const hint = (c) => (c.disabled ? t(c.reason) : methodHint(c));

// The document page's signing entry (owner, 2026-10-18): one "Sign" button
// opening a menu of every way to sign - each with its signing tool's name, its
// kind and what it means; one that cannot serve this document disabled with
// the reason. No hidden default: nobody signs with a seal by accident. A
// single way with nothing to explain: the button names it and starts at once.
// On a phone the menu is a bottom sheet; eID (a desktop application) is left
// out below 960 px, as its button always was (.signature-button).
//
// props: choices (signingChoices), loading, disabled, onChoose(choice)
export default class SignMenu extends React.Component {
  state = { open: false };

  ref = React.createRef();

  componentDidMount() {
    document.addEventListener("mousedown", this.onOutside);
    document.addEventListener("keydown", this.onKey);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onOutside);
    document.removeEventListener("keydown", this.onKey);
  }

  items = () => Array.from((this.ref.current && this.ref.current.querySelectorAll(".sign-menu__item:not([disabled])")) || []);

  open = () => this.setState({ open: true }, () => this.items()[0] && this.items()[0].focus());

  close = (refocus) => {
    this.setState({ open: false });
    const toggle = refocus && this.ref.current && this.ref.current.querySelector(".sign-menu__toggle");
    if (toggle) toggle.focus();
  };

  onOutside = (e) => {
    if (this.state.open && this.ref.current && !this.ref.current.contains(e.target)) this.close(false);
  };

  // Escape closes, the arrows move between the ways one can take
  onKey = (e) => {
    if (!this.state.open) return;
    if (e.key === "Escape") return this.close(true);
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = this.items();
    if (!items.length) return;
    const at = items.indexOf(document.activeElement);
    const next = e.key === "ArrowDown" ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
    items[next].focus();
  };

  choose = (c) => {
    if (c.disabled) return;
    this.setState({ open: false });
    this.props.onChoose(c);
  };

  render() {
    const { choices, loading, disabled } = this.props;
    if (!choices || !choices.length) return null;
    const enabled = choices.filter((c) => !c.disabled);
    if (choices.length === 1 && enabled.length === 1) {
      const c = enabled[0];
      return (
        <LoadingButton
          type="button"
          className={`button background-color-two color-white download ${c.eid ? "signature-button" : ""}`}
          loading={loading}
          disabled={disabled}
          onClick={() => this.choose(c)}
        >
          {t("withChoice", title(c))}
        </LoadingButton>
      );
    }
    const open = this.state.open;
    return (
      <div className={`sign-menu ${translateService.key}`} ref={this.ref}>
        <LoadingButton
          type="button"
          className="button background-color-two color-white download sign-menu__toggle"
          loading={loading}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => (open ? this.close(false) : this.open())}
        >
          {t("button")} <span aria-hidden="true">▾</span>
        </LoadingButton>
        {open && (
          <React.Fragment>
            {/* a phone's bottom sheet dims the page */}
            <div className="sign-menu__backdrop" aria-hidden="true" onClick={() => this.close(false)} />
            <ul className="sign-menu__list" role="menu" aria-label={t("menuLabel")}>
              {choices.map((c) => {
                const kind = kindLabel(c);
                return (
                  <li key={c.key} role="none" className={c.eid ? "signature-button" : ""}>
                    <button
                      type="button"
                      role="menuitem"
                      className="sign-menu__item"
                      disabled={c.disabled}
                      aria-disabled={c.disabled}
                      onClick={() => this.choose(c)}
                    >
                      <span className="sign-menu__title">
                        {title(c)}
                        {kind && <span className="sign-menu__kind"> · {kind}</span>}
                      </span>
                      <span className="sign-menu__hint">{hint(c)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </React.Fragment>
        )}
      </div>
    );
  }
}
